import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { content } from "@/content/fitswag";

const Logo = () => (
  <span className="flex items-center gap-2">
    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-orange">
      <span className="font-display text-lg leading-none text-black">F</span>
    </span>
    <span className="font-display text-xl tracking-tight text-white">
      Fit<span className="text-brand-orange">Swag</span>
    </span>
  </span>
);

export default function Navbar() {
  const { lang, setLang } = useLanguage();
  const t = content[lang].nav;
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const goSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") navigate("/#" + id);
    else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const links = [
    { key: "features", label: t.features, onClick: () => goSection("features") },
    { key: "swag", label: t.swag, onClick: () => goSection("swag") },
    { key: "piano", label: t.piano, onClick: () => goSection("piano") },
    { key: "faq", label: t.faq, onClick: () => goSection("faq") },
  ];

  return (
    <header
      data-testid="navbar"
      className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/60 backdrop-blur-2xl saturate-150"
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:px-8">
        <Link to="/" data-testid="nav-logo" onClick={() => setOpen(false)} className="shrink-0">
          <Logo />
        </Link>

        <div className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <button
              key={l.key}
              data-testid={`nav-link-${l.key}`}
              onClick={l.onClick}
              className="text-sm font-semibold text-white/70 transition-colors hover:text-white"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center rounded-full border border-white/15 p-0.5 text-xs font-bold">
            {["it", "en"].map((code) => (
              <button
                key={code}
                data-testid={`lang-${code}`}
                onClick={() => setLang(code)}
                className={`rounded-full px-2.5 py-1 uppercase transition-colors ${
                  lang === code ? "bg-brand-orange text-black" : "text-white/60 hover:text-white"
                }`}
              >
                {code}
              </button>
            ))}
          </div>

          <button
            data-testid="nav-cta"
            onClick={() => goSection("download")}
            className="hidden rounded-full bg-brand-orange px-5 py-2 text-sm font-bold text-white transition-transform hover:-translate-y-0.5 active:scale-[0.98] sm:block"
          >
            {t.download}
          </button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="text-white md:hidden"
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="border-t border-white/10 bg-black/95 px-5 py-4 md:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-1">
            {links.map((l) => (
              <button
                key={l.key}
                data-testid={`mobile-link-${l.key}`}
                onClick={l.onClick}
                className="w-full py-3 text-left text-lg font-bold text-white/80 transition-colors hover:text-white"
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => goSection("download")}
              className="mt-2 rounded-full bg-brand-orange px-5 py-3 text-center text-sm font-bold text-white"
            >
              {t.download}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
